import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  Search, 
  FolderTree,
  TrendingUp,
  DollarSign,
  Package,
  RefreshCw,
  ExternalLink,
  Download,
  Filter,
  CheckCircle,
  AlertTriangle,
  Calendar,
  BarChart3,
  Target,
  Users,
  Building2,
  Briefcase
} from "lucide-react";
import { toast } from "sonner";
import { useNavigation } from "@/components/layout/app-layout";

// Types
interface ProjectBudget {
  totalBudget: number;
  spent: number;
  committed: number;
  available: number;
  currency: string;
}

interface ProjectMetrics {
  totalPurchaseOrders: number;
  totalSalesOrders: number;
  totalPurchaseValue: number;
  totalSalesValue: number;
  profitMargin: number;
}

interface Project {
  id: string;
  code: string;
  name: string;
  description?: string;
  type: "project" | "cost-center";
  status: "active" | "inactive" | "completed" | "on-hold";
  category?: string;
  department?: string;
  manager?: string;
  startDate?: Date;
  endDate?: Date;
  budget?: ProjectBudget;
  metrics: ProjectMetrics;
  syncedFrom: string;
  lastSyncDate: Date;
  createdDate: Date;
  notes?: string;
}

// Mock data
const MOCK_PROJECTS: Project[] = [
  {
    id: "1",
    code: "PRJ-001",
    name: "Website Redesign 2024",
    description: "Complete overhaul of company website with new branding and e-commerce functionality",
    type: "project",
    status: "active",
    category: "IT",
    department: "Marketing",
    manager: "Sarah Johnson",
    startDate: new Date("2024-01-01"),
    endDate: new Date("2024-06-30"),
    budget: {
      totalBudget: 150000.00,
      spent: 45000.00,
      committed: 30000.00,
      available: 75000.00,
      currency: "GBP"
    },
    metrics: {
      totalPurchaseOrders: 12,
      totalSalesOrders: 0,
      totalPurchaseValue: 75000.00,
      totalSalesValue: 0,
      profitMargin: 0
    },
    syncedFrom: "Access Dimensions",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-12-01"),
    notes: "High priority project. Monthly review meetings scheduled."
  },
  {
    id: "2",
    code: "PRJ-002",
    name: "Factory Expansion Phase 2",
    description: "Expansion of manufacturing facility including new production lines",
    type: "project",
    status: "active",
    category: "Capital Projects",
    department: "Operations",
    manager: "Michael Chen",
    startDate: new Date("2023-09-01"),
    endDate: new Date("2024-12-31"),
    budget: {
      totalBudget: 500000.00,
      spent: 280000.00,
      committed: 120000.00,
      available: 100000.00,
      currency: "GBP"
    },
    metrics: {
      totalPurchaseOrders: 45,
      totalSalesOrders: 0,
      totalPurchaseValue: 400000.00,
      totalSalesValue: 0,
      profitMargin: 0
    },
    syncedFrom: "Access Dimensions",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-08-15"),
    notes: "Major capital project. Board approval required for budget changes."
  },
  {
    id: "3",
    code: "PRJ-003",
    name: "Client ABC - Custom Solution",
    description: "Bespoke manufacturing solution for Client ABC",
    type: "project",
    status: "active",
    category: "Client Projects",
    department: "Sales",
    manager: "Emma Wilson",
    startDate: new Date("2024-01-15"),
    endDate: new Date("2024-04-30"),
    budget: {
      totalBudget: 85000.00,
      spent: 25000.00,
      committed: 15000.00,
      available: 45000.00,
      currency: "GBP"
    },
    metrics: {
      totalPurchaseOrders: 8,
      totalSalesOrders: 3,
      totalPurchaseValue: 40000.00,
      totalSalesValue: 85000.00,
      profitMargin: 52.9
    },
    syncedFrom: "Access Dimensions",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2024-01-10"),
    notes: "Revenue-generating project. Good profit margin."
  },
  {
    id: "4",
    code: "CC-001",
    name: "Marketing Department",
    description: "Marketing and advertising cost center",
    type: "cost-center",
    status: "active",
    category: "Operational",
    department: "Marketing",
    budget: {
      totalBudget: 120000.00,
      spent: 85000.00,
      committed: 15000.00,
      available: 20000.00,
      currency: "GBP"
    },
    metrics: {
      totalPurchaseOrders: 28,
      totalSalesOrders: 0,
      totalPurchaseValue: 100000.00,
      totalSalesValue: 0,
      profitMargin: 0
    },
    syncedFrom: "Access Dimensions",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-01-01"),
    notes: "Annual budget allocation. Quarterly reviews."
  },
  {
    id: "5",
    code: "CC-002",
    name: "IT Operations",
    description: "IT infrastructure and support cost center",
    type: "cost-center",
    status: "active",
    category: "Operational",
    department: "IT",
    budget: {
      totalBudget: 200000.00,
      spent: 145000.00,
      committed: 25000.00,
      available: 30000.00,
      currency: "GBP"
    },
    metrics: {
      totalPurchaseOrders: 42,
      totalSalesOrders: 0,
      totalPurchaseValue: 170000.00,
      totalSalesValue: 0,
      profitMargin: 0
    },
    syncedFrom: "Access Dimensions",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-01-01"),
    notes: "Includes software licenses, hardware, and support contracts."
  },
  {
    id: "6",
    code: "CC-003",
    name: "R&D Department",
    description: "Research and development activities",
    type: "cost-center",
    status: "active",
    category: "Operational",
    department: "R&D",
    budget: {
      totalBudget: 180000.00,
      spent: 95000.00,
      committed: 35000.00,
      available: 50000.00,
      currency: "GBP"
    },
    metrics: {
      totalPurchaseOrders: 35,
      totalSalesOrders: 0,
      totalPurchaseValue: 130000.00,
      totalSalesValue: 0,
      profitMargin: 0
    },
    syncedFrom: "Access Dimensions",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-01-01"),
    notes: "Innovation budget. Patent applications in progress."
  },
  {
    id: "7",
    code: "PRJ-004",
    name: "ERP System Upgrade",
    description: "Upgrade to latest version of Access Financials",
    type: "project",
    status: "completed",
    category: "IT",
    department: "IT",
    manager: "David Brown",
    startDate: new Date("2023-06-01"),
    endDate: new Date("2023-12-31"),
    budget: {
      totalBudget: 75000.00,
      spent: 72000.00,
      committed: 0,
      available: 3000.00,
      currency: "GBP"
    },
    metrics: {
      totalPurchaseOrders: 15,
      totalSalesOrders: 0,
      totalPurchaseValue: 72000.00,
      totalSalesValue: 0,
      profitMargin: 0
    },
    syncedFrom: "Access Dimensions",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-05-15"),
    notes: "Successfully completed under budget. Post-implementation review completed."
  },
  {
    id: "8",
    code: "PRJ-005",
    name: "New Product Launch - Widget Pro",
    description: "Development and launch of Widget Pro product line",
    type: "project",
    status: "on-hold",
    category: "Product Development",
    department: "Product",
    manager: "Lisa Anderson",
    startDate: new Date("2024-02-01"),
    endDate: new Date("2024-08-31"),
    budget: {
      totalBudget: 120000.00,
      spent: 15000.00,
      committed: 5000.00,
      available: 100000.00,
      currency: "GBP"
    },
    metrics: {
      totalPurchaseOrders: 3,
      totalSalesOrders: 0,
      totalPurchaseValue: 20000.00,
      totalSalesValue: 0,
      profitMargin: 0
    },
    syncedFrom: "Access Dimensions",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2024-01-20"),
    notes: "On hold pending market research results. Review scheduled for March."
  },
  {
    id: "9",
    code: "CC-004",
    name: "Facilities Management",
    description: "Building maintenance and facilities cost center",
    type: "cost-center",
    status: "active",
    category: "Operational",
    department: "Facilities",
    budget: {
      totalBudget: 95000.00,
      spent: 68000.00,
      committed: 12000.00,
      available: 15000.00,
      currency: "GBP"
    },
    metrics: {
      totalPurchaseOrders: 52,
      totalSalesOrders: 0,
      totalPurchaseValue: 80000.00,
      totalSalesValue: 0,
      profitMargin: 0
    },
    syncedFrom: "Access Dimensions",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-01-01"),
    notes: "Includes utilities, cleaning, security, and maintenance."
  },
  {
    id: "10",
    code: "CC-005",
    name: "Human Resources",
    description: "HR operations and recruitment cost center",
    type: "cost-center",
    status: "inactive",
    category: "Operational",
    department: "HR",
    budget: {
      totalBudget: 65000.00,
      spent: 65000.00,
      committed: 0,
      available: 0,
      currency: "GBP"
    },
    metrics: {
      totalPurchaseOrders: 18,
      totalSalesOrders: 0,
      totalPurchaseValue: 65000.00,
      totalSalesValue: 0,
      profitMargin: 0
    },
    syncedFrom: "Access Dimensions",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-01-01"),
    notes: "Closed for year-end. New budget allocation pending."
  }
];

export function ProjectsCostCentersPage() {
  const { navigateTo } = useNavigation();
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);

  // Calculate summary statistics
  const stats = useMemo(() => {
    const activeProjects = MOCK_PROJECTS.filter(p => p.type === "project" && p.status === "active").length;
    const activeCostCenters = MOCK_PROJECTS.filter(p => p.type === "cost-center" && p.status === "active").length;
    const totalBudget = MOCK_PROJECTS
      .filter(p => p.status === "active" && p.budget)
      .reduce((sum, p) => sum + (p.budget?.totalBudget || 0), 0);
    const totalSpent = MOCK_PROJECTS
      .filter(p => p.status === "active" && p.budget)
      .reduce((sum, p) => sum + (p.budget?.spent || 0), 0);

    return { activeProjects, activeCostCenters, totalBudget, totalSpent };
  }, []);

  // Get unique categories for filter
  const categories = useMemo(() => {
    const uniqueCategories = Array.from(new Set(MOCK_PROJECTS.map(p => p.category).filter(Boolean)));
    return uniqueCategories.sort();
  }, []);

  // Filter data
  const filteredData = useMemo(() => {
    let filtered = MOCK_PROJECTS;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(project =>
        project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.department?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by type
    if (typeFilter !== "all") {
      filtered = filtered.filter(project => project.type === typeFilter);
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(project => project.status === statusFilter);
    }

    // Filter by category
    if (categoryFilter !== "all") {
      filtered = filtered.filter(project => project.category === categoryFilter);
    }

    return filtered;
  }, [searchTerm, typeFilter, statusFilter, categoryFilter]);

  // Format date
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  // Format date and time
  const formatDateTime = (date: Date) => {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string = "GBP") => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge variant="success" accent="success">Active</Badge>;
      case "inactive":
        return <Badge variant="secondary">Inactive</Badge>;
      case "completed":
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Completed</Badge>;
      case "on-hold":
        return <Badge variant="destructive">On Hold</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Get type badge
  const getTypeBadge = (type: string) => {
    switch (type) {
      case "project":
        return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Project</Badge>;
      case "cost-center":
        return <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">Cost Center</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  // Get budget health indicator
  const getBudgetHealthBadge = (budget: ProjectBudget) => {
    const utilization = ((budget.spent + budget.committed) / budget.totalBudget) * 100;
    
    if (utilization >= 95) {
      return <Badge variant="destructive">Critical</Badge>;
    } else if (utilization >= 80) {
      return <Badge variant="secondary" className="bg-yellow-50 text-yellow-700 border-yellow-200">Warning</Badge>;
    } else {
      return <Badge variant="success" accent="success">Healthy</Badge>;
    }
  };

  // Handle row click
  const handleRowClick = (project: Project) => {
    setSelectedProject(project);
    setShowDetailDrawer(true);
  };

  // Handle sync
  const handleSync = () => {
    toast.promise(
      new Promise((resolve) => setTimeout(resolve, 1500)),
      {
        loading: "Syncing with Access Dimensions...",
        success: "Projects & Cost Centers synced successfully",
        error: "Failed to sync",
      }
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Projects & Cost Centers</h1>
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                Synced from Access Dimensions
              </Badge>
            </div>
            <p className="text-muted-foreground mt-2">
              View and manage projects and cost centers synced from your ERP system
            </p>
          </div>
          <Button onClick={handleSync} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Sync Now
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Projects</CardTitle>
            <Briefcase className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{stats.activeProjects}</div>
            <p className="text-xs text-muted-foreground">
              In progress
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Cost Centers</CardTitle>
            <Building2 className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats.activeCostCenters}</div>
            <p className="text-xs text-muted-foreground">
              Operational
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Budget</CardTitle>
            <DollarSign className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(stats.totalBudget)}
            </div>
            <p className="text-xs text-muted-foreground">
              Active budgets
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Spent</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(stats.totalSpent)}
            </div>
            <p className="text-xs text-muted-foreground">
              {((stats.totalSpent / stats.totalBudget) * 100).toFixed(1)}% of budget
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Projects & Cost Centers Directory</CardTitle>
              <CardDescription>
                View project and cost center information synced from Access Dimensions
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, code, description, or department..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="project">Projects</SelectItem>
                <SelectItem value="cost-center">Cost Centers</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="on-hold">On Hold</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Results count */}
          <div className="text-sm text-muted-foreground mb-4">
            Showing {filteredData.length} of {MOCK_PROJECTS.length} items
          </div>

          {/* Data Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Budget</TableHead>
                  <TableHead className="text-right">Spent</TableHead>
                  <TableHead className="text-center">Health</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No projects or cost centers found matching your criteria
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((project) => {
                    return (
                      <TableRow 
                        key={project.id} 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleRowClick(project)}
                      >
                        <TableCell>
                          <div className="font-mono font-medium">{project.code}</div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{project.name}</div>
                            {project.description && (
                              <div className="text-sm text-muted-foreground line-clamp-1">
                                {project.description}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getTypeBadge(project.type)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <span>{project.department || "-"}</span>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(project.status)}</TableCell>
                        <TableCell className="text-right">
                          {project.budget ? (
                            <div className="font-mono font-medium">
                              {formatCurrency(project.budget.totalBudget, project.budget.currency)}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {project.budget ? (
                            <div>
                              <div className="font-mono font-medium">
                                {formatCurrency(project.budget.spent, project.budget.currency)}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {((project.budget.spent / project.budget.totalBudget) * 100).toFixed(1)}%
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {project.budget && getBudgetHealthBadge(project.budget)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateTo("purchase-orders");
                              }}
                            >
                              <Package className="h-4 w-4 mr-1" />
                              Orders
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Detail Drawer */}
      <Sheet open={showDetailDrawer} onOpenChange={setShowDetailDrawer}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto p-3">
          {selectedProject && (
            <>
              <SheetHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <SheetTitle>{selectedProject.name}</SheetTitle>
                    {getStatusBadge(selectedProject.status)}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => toast.info("View in Access Dimensions")}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View in ERP
                  </Button>
                </div>
                <SheetDescription>
                  {selectedProject.code} • {getTypeBadge(selectedProject.type)}
                </SheetDescription>
              </SheetHeader>

              <Tabs defaultValue="overview" className="mt-6">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="budget">Budget</TabsTrigger>
                  <TabsTrigger value="activity">Activity</TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-6">
                  {/* Sync Information */}
                  <div className="bg-muted/50 p-3 rounded-lg text-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <RefreshCw className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Last synced:</span>
                        <span className="font-medium">{formatDateTime(selectedProject.lastSyncDate)}</span>
                      </div>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        {selectedProject.syncedFrom}
                      </Badge>
                    </div>
                  </div>

                  {/* Basic Information */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <FolderTree className="h-4 w-4" />
                      Basic Information
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Code</Label>
                        <p className="font-medium font-mono">{selectedProject.code}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Name</Label>
                        <p className="font-medium">{selectedProject.name}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Type</Label>
                        <div className="mt-1">{getTypeBadge(selectedProject.type)}</div>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Status</Label>
                        <div className="mt-1">{getStatusBadge(selectedProject.status)}</div>
                      </div>
                      {selectedProject.category && (
                        <div>
                          <Label className="text-muted-foreground">Category</Label>
                          <p className="font-medium">{selectedProject.category}</p>
                        </div>
                      )}
                      {selectedProject.department && (
                        <div>
                          <Label className="text-muted-foreground">Department</Label>
                          <p className="font-medium">{selectedProject.department}</p>
                        </div>
                      )}
                      {selectedProject.manager && (
                        <div className="col-span-2">
                          <Label className="text-muted-foreground">Project Manager</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <p className="font-medium">{selectedProject.manager}</p>
                          </div>
                        </div>
                      )}
                      {selectedProject.description && (
                        <div className="col-span-2">
                          <Label className="text-muted-foreground">Description</Label>
                          <p className="text-sm mt-1">{selectedProject.description}</p>
                        </div>
                      )}
                      <div>
                        <Label className="text-muted-foreground">Created Date</Label>
                        <p className="font-medium">{formatDate(selectedProject.createdDate)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Timeline (for projects) */}
                  {selectedProject.type === "project" && selectedProject.startDate && (
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Timeline
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-muted-foreground">Start Date</Label>
                          <p className="font-medium">{formatDate(selectedProject.startDate)}</p>
                        </div>
                        {selectedProject.endDate && (
                          <div>
                            <Label className="text-muted-foreground">End Date</Label>
                            <p className="font-medium">{formatDate(selectedProject.endDate)}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {selectedProject.notes && (
                    <div>
                      <h3 className="font-semibold mb-3">Notes</h3>
                      <p className="text-sm bg-muted p-3 rounded-md">{selectedProject.notes}</p>
                    </div>
                  )}
                </TabsContent>

                {/* Budget Tab */}
                <TabsContent value="budget" className="space-y-6">
                  {selectedProject.budget ? (
                    <>
                      {/* Budget Overview */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card>
                          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Total Budget</CardTitle>
                            <Target className="h-4 w-4 text-muted-foreground" />
                          </CardHeader>
                          <CardContent>
                            <div className="text-2xl font-bold">
                              {formatCurrency(selectedProject.budget.totalBudget, selectedProject.budget.currency)}
                            </div>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Spent</CardTitle>
                            <DollarSign className="h-4 w-4 text-red-600" />
                          </CardHeader>
                          <CardContent>
                            <div className="text-2xl font-bold text-red-600">
                              {formatCurrency(selectedProject.budget.spent, selectedProject.budget.currency)}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {((selectedProject.budget.spent / selectedProject.budget.totalBudget) * 100).toFixed(1)}% of budget
                            </p>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Committed</CardTitle>
                            <AlertTriangle className="h-4 w-4 text-yellow-600" />
                          </CardHeader>
                          <CardContent>
                            <div className="text-2xl font-bold text-yellow-600">
                              {formatCurrency(selectedProject.budget.committed, selectedProject.budget.currency)}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              Pending orders
                            </p>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Available</CardTitle>
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          </CardHeader>
                          <CardContent>
                            <div className="text-2xl font-bold text-green-600">
                              {formatCurrency(selectedProject.budget.available, selectedProject.budget.currency)}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              Remaining budget
                            </p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Budget Utilisation */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-sm font-medium">Budget Utilisation</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {/* Spent */}
                          <div>
                            <div className="flex items-center justify-between text-sm mb-2">
                              <span className="font-medium">Spent</span>
                              <span className="text-muted-foreground">
                                {formatCurrency(selectedProject.budget.spent, selectedProject.budget.currency)} 
                                ({((selectedProject.budget.spent / selectedProject.budget.totalBudget) * 100).toFixed(1)}%)
                              </span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div 
                                className="bg-red-600 h-2 rounded-full"
                                style={{ 
                                  width: `${Math.min((selectedProject.budget.spent / selectedProject.budget.totalBudget) * 100, 100)}%` 
                                }}
                              />
                            </div>
                          </div>

                          {/* Committed */}
                          <div>
                            <div className="flex items-center justify-between text-sm mb-2">
                              <span className="font-medium">Committed</span>
                              <span className="text-muted-foreground">
                                {formatCurrency(selectedProject.budget.committed, selectedProject.budget.currency)}
                                ({((selectedProject.budget.committed / selectedProject.budget.totalBudget) * 100).toFixed(1)}%)
                              </span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div 
                                className="bg-yellow-600 h-2 rounded-full"
                                style={{ 
                                  width: `${Math.min((selectedProject.budget.committed / selectedProject.budget.totalBudget) * 100, 100)}%` 
                                }}
                              />
                            </div>
                          </div>

                          {/* Total Utilisation */}
                          <div className="pt-4 border-t">
                            <div className="flex items-center justify-between text-sm mb-2">
                              <span className="font-medium">Total Utilisation (Spent + Committed)</span>
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">
                                  {formatCurrency(selectedProject.budget.spent + selectedProject.budget.committed, selectedProject.budget.currency)}
                                  ({(((selectedProject.budget.spent + selectedProject.budget.committed) / selectedProject.budget.totalBudget) * 100).toFixed(1)}%)
                                </span>
                                {getBudgetHealthBadge(selectedProject.budget)}
                              </div>
                            </div>
                            <div className="w-full bg-muted rounded-full h-3">
                              <div 
                                className={`h-3 rounded-full ${
                                  ((selectedProject.budget.spent + selectedProject.budget.committed) / selectedProject.budget.totalBudget) >= 0.95
                                    ? 'bg-destructive'
                                    : ((selectedProject.budget.spent + selectedProject.budget.committed) / selectedProject.budget.totalBudget) >= 0.80
                                    ? 'bg-yellow-500'
                                    : 'bg-green-600'
                                }`}
                                style={{ 
                                  width: `${Math.min(((selectedProject.budget.spent + selectedProject.budget.committed) / selectedProject.budget.totalBudget) * 100, 100)}%` 
                                }}
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No budget information available</p>
                    </div>
                  )}
                </TabsContent>

                {/* Activity Tab */}
                <TabsContent value="activity" className="space-y-6">
                  {/* Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Purchase Orders</CardTitle>
                        <Package className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {selectedProject.metrics.totalPurchaseOrders}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Total value: {formatCurrency(selectedProject.metrics.totalPurchaseValue, selectedProject.budget?.currency || "GBP")}
                        </p>
                      </CardContent>
                    </Card>

                    {selectedProject.type === "project" && (
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium">Sales Orders</CardTitle>
                          <BarChart3 className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold">
                            {selectedProject.metrics.totalSalesOrders}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Total value: {formatCurrency(selectedProject.metrics.totalSalesValue, selectedProject.budget?.currency || "GBP")}
                          </p>
                        </CardContent>
                      </Card>
                    )}

                    {selectedProject.type === "project" && selectedProject.metrics.totalSalesValue > 0 && (
                      <Card className="md:col-span-2">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium">Profit Margin</CardTitle>
                          <TrendingUp className="h-4 w-4 text-green-600" />
                        </CardHeader>
                        <CardContent>
                          <div className="text-3xl font-bold text-green-600">
                            {selectedProject.metrics.profitMargin.toFixed(1)}%
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Revenue: {formatCurrency(selectedProject.metrics.totalSalesValue, selectedProject.budget?.currency || "GBP")} - 
                            Cost: {formatCurrency(selectedProject.metrics.totalPurchaseValue, selectedProject.budget?.currency || "GBP")}
                          </p>
                        </CardContent>
                      </Card>
                    )}
                  </div>

                  {/* Quick Actions */}
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        navigateTo("purchase-orders");
                        setShowDetailDrawer(false);
                      }}
                    >
                      <Package className="h-4 w-4 mr-2" />
                      View Purchase Orders
                    </Button>
                    {selectedProject.type === "project" && (
                      <Button 
                        variant="outline"
                        className="flex-1"
                        onClick={() => {
                          navigateTo("sales-orders");
                          setShowDetailDrawer(false);
                        }}
                      >
                        <BarChart3 className="h-4 w-4 mr-2" />
                        View Sales Orders
                      </Button>
                    )}
                  </div>
                </TabsContent>
              </Tabs>

              {/* Actions */}
              <div className="flex gap-2 pt-6 border-t mt-6">
                <Button 
                  className="flex-1"
                  onClick={() => {
                    navigateTo("purchase-orders");
                    setShowDetailDrawer(false);
                  }}
                >
                  <Package className="h-4 w-4 mr-2" />
                  View Related Orders
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => toast.info("View in Access Dimensions")}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View in ERP
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
